import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["loadingIndicator", "submitButton", "results"]
  static values = {
    delay: { type: Number, default: 500 } // Increased delay for better UX
  }

  connect() {
    this.timeout = null
    this.isSubmitting = false
  }

  disconnect() {
    clearTimeout(this.timeout)
  }

  // Enhanced submit with loading states
  submit() {
    // Don't submit if already submitting
    if (this.isSubmitting) {
      return
    }

    clearTimeout(this.timeout)
    
    // Show immediate feedback for user interaction
    this.showPendingState()
    
    this.timeout = setTimeout(() => {
      this.performSubmit()
    }, this.delayValue)
  }

  // Force immediate submit (for button clicks)
  submitNow() {
    clearTimeout(this.timeout)
    this.performSubmit()
  }

  performSubmit() {
    if (this.isSubmitting) return
    
    this.isSubmitting = true
    this.showLoadingState()
    
    // Add loading class to results area
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add('opacity-50', 'pointer-events-none')
    }
    
    this.element.requestSubmit()
  }

  showPendingState() {
    // Show subtle indication that input is being processed
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
      this.loadingIndicatorTarget.innerHTML = `
        <div class="flex items-center text-xs text-zinc-500">
          <svg class="animate-spin -ml-1 mr-2 h-3 w-3 text-zinc-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Filtering...
        </div>
      `
    }
  }

  showLoadingState() {
    // Update submit button state
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add('opacity-75', 'cursor-not-allowed')
      
      const originalText = this.submitButtonTarget.innerHTML
      this.submitButtonTarget.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Filtering Rentals...
      `
      
      // Store original text for restoration
      this.originalButtonText = originalText
    }

    // Enhanced loading indicator
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
      this.loadingIndicatorTarget.innerHTML = `
        <div class="flex items-center justify-center p-4 text-sm text-zinc-600 dark:text-zinc-400">
          <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Checking availability and filtering rentals...
        </div>
      `
    }
  }

  // Called when form submission completes (via Turbo)
  resetLoadingState() {
    this.isSubmitting = false
    
    // Reset submit button
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('opacity-75', 'cursor-not-allowed')
      
      if (this.originalButtonText) {
        this.submitButtonTarget.innerHTML = this.originalButtonText
      }
    }

    // Hide loading indicator
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }

    // Reset results area
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.remove('opacity-50', 'pointer-events-none')
    }
  }

  // Listen for Turbo events to reset state
  turboSubmitEnd() {
    this.resetLoadingState()
  }

  turboSubmitStart() {
    // Ensure loading state is shown even if not triggered by our submit
    if (!this.isSubmitting) {
      this.isSubmitting = true
      this.showLoadingState()
    }
  }
}
